import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { isPast } from 'date-fns';

// Routes that org_admins can still access even when trial is expired
const TRIAL_EXEMPT_PATHS = [
  '/subscription-billing',
  '/subscription-management',
  '/login',
  '/logout',
  '/paywall',
  '/reset-password',
  '/forgot-password',
  '/org-admin-first-login-checklist',
];

// Maximum time to wait for subscription check before unblocking
const TRIAL_CHECK_TIMEOUT_MS = 6000;

/**
 * useTrialGuard
 *
 * Returns { blocked, loading, onActivated } for the current authenticated user.
 * - blocked: true when the user is an org_admin whose trial has expired (or account is suspended)
 *            AND the current route is not in TRIAL_EXEMPT_PATHS.
 * - loading: true while the subscription status is being fetched.
 * - onActivated: callback to call after the user successfully upgrades so the block is lifted.
 *
 * Superadmins are never blocked.
 */
const useTrialGuard = () => {
  const { userProfile, isSuperAdmin } = useAuth();
  const location = useLocation();
  const [blocked, setBlocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const safetyTimerRef = useRef(null);

  const isOrgAdmin = userProfile?.system_role === 'org_admin';
  const isExemptPath = TRIAL_EXEMPT_PATHS?.some(
    (p) => location?.pathname === p || location?.pathname?.startsWith(p + '/')
  );

  const checkSubscription = useCallback(async () => {
    // Only check for org_admins on non-exempt paths
    if (!isOrgAdmin || isSuperAdmin || isExemptPath) {
      setBlocked(false);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Safety valve: never block the app for more than TRIAL_CHECK_TIMEOUT_MS
    if (safetyTimerRef?.current) clearTimeout(safetyTimerRef?.current);
    safetyTimerRef.current = setTimeout(() => {
      setLoading(false);
    }, TRIAL_CHECK_TIMEOUT_MS);

    try {
      const orgId = userProfile?.organization_id;
      if (!orgId) {
        setBlocked(false);
        return;
      }

      const { data: sub } = await supabase
        ?.from('org_subscriptions')
        ?.select('status, trial_ends_at, account_suspended_at, payment_method_added')
        ?.eq('organization_id', orgId)
        ?.maybeSingle();

      if (!sub) {
        setBlocked(false);
        return;
      }

      const suspended = !!sub?.account_suspended_at;
      const status = sub?.status;
      const trialEnd = sub?.trial_ends_at ? new Date(sub.trial_ends_at) : null;

      const trialExpired =
        status === 'trialing' && trialEnd && isPast(trialEnd);

      const pastDueOrCancelled =
        status === 'past_due' || status === 'cancelled';

      setBlocked(trialExpired || suspended || pastDueOrCancelled);
    } catch (err) {
      console.error('[TrialGuard] Subscription check error:', err?.message);
      setBlocked(false);
    } finally {
      if (safetyTimerRef?.current) {
        clearTimeout(safetyTimerRef?.current);
        safetyTimerRef.current = null;
      }
      setLoading(false);
    }
  }, [isOrgAdmin, isSuperAdmin, isExemptPath, userProfile?.organization_id]);

  useEffect(() => {
    if (userProfile) {
      checkSubscription();
    } else {
      // No profile yet — don't block
      setLoading(false);
      setBlocked(false);
    }

    return () => {
      if (safetyTimerRef?.current) clearTimeout(safetyTimerRef?.current);
    };
  }, [userProfile, checkSubscription]);

  const onActivated = useCallback(() => {
    setBlocked(false);
  }, []);

  return { blocked, loading, onActivated, refresh: checkSubscription };
};

export default useTrialGuard;
